import React, {
  useState
} from 'react';

import {
  NavLink,
  useLocation
} from 'react-router-dom';

import {
  AnimatePresence,
  motion
} from 'motion/react';

import {
  LayoutDashboard,
  FolderKanban,
  Terminal,
  MoreHorizontal,
  Files,
  CloudUpload,
  History,
  Settings,
  Bot,
  MessageSquareText,
  X,
  ChevronRight
} from 'lucide-react';
import { MonitorUp } from 'lucide-react';

import {
  kaizenMicroTransition,
  kaizenNavTransition,
  kaizenOverlayTransition,
  kaizenSheetTransition
} from '../../lib/kaizenMotion';


const primaryItems = [

  {
    name:
      'Home',

    to:
      '/',

    icon:
      LayoutDashboard
  },

  {
    name:
      'Projects',

    to:
      '/projects',

    icon:
      FolderKanban
  },

  {
    name:
      'Console',

    to:
      '/console',

    icon:
      Terminal
  },

  {
    name:
      'JARVIS',

    to:
      '/assistant',

    icon:
      Bot
  }

];


const secondaryItems = [

  {
    name:
      'ChatGPT',

    description:
      'Direct ChatGPT workspace',

    to:
      '/chatgpt',

    icon:
      MessageSquareText
  },
  // KAIZEN_REMOTE_DESKTOP_MOBILE_FINAL
  {
    name:
      'Remote Desktop',

    description:
      'Live Windows workspace',

    to:
      '/remote-desktop',

    icon:
      MonitorUp
  },

  {
    name:
      'Files & Reports',

    description:
      'Artifacts and diagnostics',

    to:
      '/files',

    icon:
      Files
  },

  {
    name:
      'Deployments',

    description:
      'Preview and production',

    to:
      '/deployments',

    icon:
      CloudUpload
  },

  {
    name:
      'History',

    description:
      'Execution audit trail',

    to:
      '/history',

    icon:
      History
  },

  {
    name:
      'Settings',

    description:
      'Operator and host controls',

    to:
      '/settings',

    icon:
      Settings
  }

];


export const MobileBottomNav:
React.FC = () => {

  const [
    moreOpen,
    setMoreOpen
  ] =
    useState(
      false
    );


  const location =
    useLocation();


  const secondaryActive =
    secondaryItems.some(
      item =>
        location.pathname ===
          item.to ||
        location.pathname.startsWith(
          `${item.to}/`
        )
    );


  return (

    <>

      {/* MORE */}

      <AnimatePresence initial={false}>
        {moreOpen && (

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={kaizenOverlayTransition}
          className="
            md:hidden
            fixed inset-0
            z-[70]
            bg-slate-950/60
            backdrop-blur-xl
          "
          onClick={() =>
            setMoreOpen(
              false
            )
          }
        >

          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute
              left-1/2 bottom-20
              -translate-x-1/2
              w-72 h-72
              rounded-full
              bg-indigo-500/10
              blur-3xl
            "
          />


          <motion.div
            initial={{
              opacity: 0,
              y: 18,
              scale: 0.992
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }}
            exit={{
              opacity: 0,
              y: 14,
              scale: 0.994
            }}
            transition={kaizenSheetTransition}
            className="
              absolute
              left-3 right-3
              bottom-[calc(5.75rem+env(safe-area-inset-bottom))]
              overflow-hidden
              rounded-[18px]
              border border-white/10
              bg-slate-900/88
              shadow-[0_28px_90px_rgba(2,6,23,0.72)]
              backdrop-blur-2xl
            "
            onClick={
              event =>
                event.stopPropagation()
            }
          >

            <div
              className="
                flex items-center justify-between
                gap-3
                px-4
                py-4
                border-b border-white/[0.07]
                bg-white/[0.025]
              "
            >

              <div>

                <h3 className="text-[15px] font-semibold text-slate-100">
                  KAIZEN Remote
                </h3>

                <p className="text-[10px] text-slate-500 mt-0.5">
                  System tools
                </p>

              </div>


              <button
                type="button"
                onClick={() =>
                  setMoreOpen(
                    false
                  )
                }
                className="
                  w-9 h-9
                  rounded-lg
                  flex items-center justify-center
                  text-slate-500
                  hover:text-slate-200
                  hover:bg-white/[0.05]
                "
              >

                <X className="w-4 h-4" />

              </button>

            </div>


            <div className="p-4">

              <div
                className="
                  overflow-hidden
                  rounded-xl
                  border border-white/[0.07]
                  bg-white/[0.025]
                "
              >

                {secondaryItems.map(
                  (
                    item,
                    index
                  ) => {

                    const Icon =
                      item.icon;


                    return (

                      <NavLink
                        key={
                          item.to
                        }
                        to={
                          item.to
                        }
                        onClick={() =>
                          setMoreOpen(
                            false
                          )
                        }
                        className={({
                          isActive
                        }) =>
                          `
                            group
                            min-h-[62px]
                            px-3.5
                            flex items-center gap-3
                            transition-colors
                            ${
                              index !== secondaryItems.length - 1
                                ? 'border-b border-white/[0.06]'
                                : ''
                            }
                            ${
                              isActive
                                ? 'bg-indigo-400/[0.07]'
                                : 'active:bg-white/[0.045]'
                            }
                          `
                        }
                      >

                        {({
                          isActive
                        }) => (

                          <>

                            <div
                              className={`
                                w-9 h-9
                                shrink-0
                                rounded-lg
                                flex items-center justify-center
                                ${
                                  isActive
                                    ? 'bg-indigo-400/10 text-indigo-300'
                                    : 'bg-white/[0.025] text-indigo-400'
                                }
                              `}
                            >

                              <Icon className="w-4 h-4" />

                            </div>


                            <div className="min-w-0 flex-1">

                              <div
                                className={`text-xs font-medium ${
                                  isActive
                                    ? 'text-indigo-200'
                                    : 'text-slate-300'
                                }`}
                              >
                                {item.name}
                              </div>

                              <div className="text-[9px] text-slate-600 mt-0.5">
                                {item.description}
                              </div>

                            </div>


                            <ChevronRight className="w-3.5 h-3.5 text-slate-700 group-active:text-indigo-400" />

                          </>

                        )}

                      </NavLink>
                    );
                  }
                )}

              </div>

            </div>

          </motion.div>

        </motion.div>

        )}
      </AnimatePresence>


      {/* FLOATING NAV DOCK */}

      <nav
        className="
          md:hidden
          fixed
          left-3 right-3
          bottom-[calc(0.65rem+env(safe-area-inset-bottom))]
          z-[60]
          overflow-hidden
          rounded-[18px]
          border border-white/10
          bg-slate-900/88
          shadow-[0_22px_70px_rgba(2,6,23,0.58)]
          backdrop-blur-2xl
        "
      >

        <div className="h-[66px] grid grid-cols-5 px-1">

          {primaryItems.map(
            item => {

              const Icon =
                item.icon;


              return (

                <NavLink
                  key={
                    item.to
                  }
                  to={
                    item.to
                  }
                  end={
                    item.to ===
                    '/'
                  }
                  className={({
                    isActive
                  }) =>
                    `
                      relative
                      flex flex-col
                      items-center justify-center
                      gap-1
                      text-[9px]
                      font-medium
                      ${
                        isActive
                          ? 'text-indigo-300'
                          : 'text-slate-600'
                      }
                    `
                  }
                >

                  {({
                    isActive
                  }) => (

                    <>

                      {isActive && (

                        <motion.span
                          layoutId="kaizen-mobile-active-rail"
                          transition={
                            kaizenNavTransition
                          }
                          className="
                            absolute
                            top-0
                            w-8 h-[2px]
                            rounded-b-full
                            bg-indigo-400
                            shadow-[0_0_14px_rgba(129,140,248,0.65)]
                          "
                        />

                      )}


                      <div
                        className={`
                          w-8 h-7
                          rounded-lg
                          flex items-center justify-center
                          ${
                            isActive
                              ? 'bg-indigo-400/[0.085]'
                              : ''
                          }
                        `}
                      >

                        <motion.span
                          className="inline-flex"
                          animate={
                            isActive
                              ? {
                                  scale: 1.08,
                                  y: -1
                                }
                              : {
                                  scale: 1,
                                  y: 0
                                }
                          }
                          transition={
                            kaizenMicroTransition
                          }
                        >
                          <Icon className="w-[18px] h-[18px]" />
                        </motion.span>

                      </div>


                      {item.name}

                    </>

                  )}

                </NavLink>
              );
            }
          )}


          <button
            type="button"
            onClick={() =>
              setMoreOpen(
                true
              )
            }
            className={`
              relative
              flex flex-col
              items-center justify-center
              gap-1
              text-[9px]
              font-medium
              ${
                secondaryActive ||
                moreOpen
                  ? 'text-indigo-300'
                  : 'text-slate-600'
              }
            `}
          >

            {(secondaryActive ||
              moreOpen) && (

              <motion.span
                layoutId="kaizen-mobile-active-rail"
                transition={
                  kaizenNavTransition
                }
                className="
                  absolute
                  top-0
                  w-8 h-[2px]
                  rounded-b-full
                  bg-indigo-400
                  shadow-[0_0_14px_rgba(129,140,248,0.65)]
                "
              />

            )}


            <div
              className={`
                w-8 h-7
                rounded-lg
                flex items-center justify-center
                ${
                  secondaryActive ||
                  moreOpen
                    ? 'bg-indigo-400/[0.085]'
                    : ''
                }
              `}
            >

              <motion.span
                className="inline-flex"
                animate={
                  secondaryActive ||
                  moreOpen
                    ? {
                        scale: 1.08,
                        y: -1
                      }
                    : {
                        scale: 1,
                        y: 0
                      }
                }
                transition={
                  kaizenMicroTransition
                }
              >
                <MoreHorizontal className="w-[18px] h-[18px]" />
              </motion.span>

            </div>


            More

          </button>

        </div>

      </nav>

    </>
  );
};